// Path: /var/www/speakiq/frontend/src/pages/admin/AdminWebsite.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', success: '#10B981',
  warning: '#F59E0B', error: '#EF4444', grey: '#6B7280', border: '#E5E7EB',
};

const DAYS = ['mon','tue','wed','thu','fri','sat','sun'];
const DAY_LABELS = { mon:'Monday', tue:'Tuesday', wed:'Wednesday', thu:'Thursday', fri:'Friday', sat:'Saturday', sun:'Sunday' };

const defaultHours = () => Object.fromEntries(DAYS.map(d => [d, {
  open: ['mon','tue','wed','thu','fri'].includes(d),
  from: d === 'sat' ? '09:00' : '09:00',
  to: d === 'sat' ? '15:00' : '19:00',
}]));

const defaultForm = () => ({
  tagline: '', description: '', founded_year: '', hero_image_url: '',
  address_line1: '', address_line2: '', city: '', region: '', phone: '',
  whatsapp: '', public_email: '', google_maps_embed: '',
  working_hours: defaultHours(),
  instagram_url: '', telegram_url: '', facebook_url: '', youtube_url: '', tiktok_url: '',
  gallery_images: [],
  testimonials: [],
  meta_title: '', meta_description: '', meta_keywords: '',
  website_enabled: false, show_prices_public: false, show_tutors_public: false,
  registration_open: true, registration_message: '',
});

function CharCounter({ value, max }) {
  const len = (value || '').length;
  return <span style={{ fontSize: 11, color: len > max * 0.9 ? C.warning : C.grey }}>{len}/{max}</span>;
}

function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display:'flex', alignItems:'center', gap: 10, cursor:'pointer' }}>
      <div onClick={() => onChange(!checked)} style={{
        width: 44, height: 24, borderRadius: 12, background: checked ? C.teal : C.border,
        position:'relative', transition:'background 0.2s', cursor:'pointer',
      }}>
        <div style={{
          width: 20, height: 20, borderRadius: '50%', background: '#fff',
          position:'absolute', top: 2, left: checked ? 22 : 2,
          transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
      <span style={{ fontSize: 14, color: C.navy }}>{label}</span>
    </label>
  );
}

function StarRating({ value, onChange }) {
  return (
    <div style={{ display:'flex', gap: 4 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n} onClick={() => onChange(n)}
          style={{ fontSize: 20, cursor:'pointer', color: n <= value ? C.warning : C.border }}>★</span>
      ))}
    </div>
  );
}

function Section({ title, open, onToggle, children }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 12, overflow:'hidden' }}>
      <div onClick={onToggle} style={{
        display:'flex', justifyContent:'space-between', alignItems:'center',
        padding:'14px 18px', background: open ? C.navy : '#f9fafb',
        cursor:'pointer', userSelect:'none',
      }}>
        <span style={{ fontWeight: 600, color: open ? '#fff' : C.navy, fontSize: 14 }}>{title}</span>
        <span style={{ color: open ? '#fff' : C.grey, fontSize: 18 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <div style={{ padding: 18, background:'#fff' }}>{children}</div>}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display:'block', fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 4 }}>{label}</label>
      {hint && <p style={{ fontSize: 11, color: C.grey, margin:'0 0 4px' }}>{hint}</p>}
      {children}
    </div>
  );
}

const inp = (extra = {}) => ({
  width:'100%', padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius: 6,
  fontSize: 13, boxSizing:'border-box', outline:'none', ...extra,
});

function LivePreview({ form, subdomain }) {
  const bg = form.hero_image_url ? `url(${form.hero_image_url}) center/cover` : `linear-gradient(135deg, ${C.navy}, ${C.teal})`;
  const openFull = () => {
    if (subdomain) window.open(`/${subdomain}`, '_blank');
    else alert('Subdomain not configured yet.');
  };
  return (
    <div style={{ position:'sticky', top: 80, background:'#f0f4f8', borderRadius: 12, overflow:'hidden', border:`1px solid ${C.border}` }}>
      <div style={{ padding:'10px 14px', background: C.navy, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ color:'#fff', fontSize: 12, fontWeight: 600 }}>Live Preview</span>
        <button onClick={openFull} style={{ background: C.teal, color:'#fff', border:'none', borderRadius: 5, padding:'4px 10px', fontSize: 11, cursor:'pointer' }}>
          View Full Site ↗
        </button>
      </div>
      <div style={{ transform:'scale(0.45)', transformOrigin:'top left', width:'222.2%', height: 480, pointerEvents:'none' }}>
        {/* Mini Nav */}
        <div style={{ display:'flex', alignItems:'center', gap: 12, padding:'10px 20px', background: C.navy }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: C.teal, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize: 14, fontWeight: 700 }}>
            {(form.name || 'S').charAt(0)}
          </div>
          <span style={{ color:'#fff', fontWeight: 700, fontSize: 16 }}>{form.name || 'Your School'}</span>
          <div style={{ marginLeft:'auto', display:'flex', gap: 12 }}>
            {['Home','Courses','Tutors','About','Contact'].map(l => (
              <span key={l} style={{ color:'rgba(255,255,255,0.7)', fontSize: 12 }}>{l}</span>
            ))}
          </div>
        </div>
        {/* Mini Hero */}
        <div style={{ background: bg, padding:'40px 20px', textAlign:'center', minHeight: 200 }}>
          <h1 style={{ color:'#fff', fontSize: 28, margin:'0 0 8px', textShadow:'0 2px 8px rgba(0,0,0,0.4)' }}>
            {form.name || 'Your School'}
          </h1>
          <p style={{ color:'rgba(255,255,255,0.9)', fontSize: 15, margin:'0 0 20px' }}>
            {form.tagline || 'Learn English with confidence'}
          </p>
          <div style={{ display:'flex', gap: 10, justifyContent:'center' }}>
            <span style={{ background:'#fff', color: C.navy, borderRadius: 20, padding:'6px 16px', fontSize: 12, fontWeight: 600 }}>Register Now →</span>
            <span style={{ border:'1px solid #fff', color:'#fff', borderRadius: 20, padding:'6px 16px', fontSize: 12 }}>Learn More ↓</span>
          </div>
        </div>
        {/* Mini Courses Row */}
        <div style={{ display:'flex', gap: 12, padding: 20, background:'#f9fafb' }}>
          {['Beginner', 'Intermediate', 'Advanced'].map(l => (
            <div key={l} style={{ flex: 1, background:'#fff', borderRadius: 8, padding: 14, border:`1px solid ${C.border}`, textAlign:'center' }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>📚</div>
              <div style={{ fontWeight: 600, fontSize: 13, color: C.navy }}>{l}</div>
              <div style={{ fontSize: 11, color: C.grey }}>CEFR Course</div>
            </div>
          ))}
        </div>
        {/* Mini Footer */}
        <div style={{ background: C.navy, padding:'14px 20px', color:'rgba(255,255,255,0.6)', fontSize: 11 }}>
          {form.city || 'City'} · {form.phone || '+998 XX XXX XX XX'} · © 2026 {form.name || 'School'}
        </div>
      </div>
    </div>
  );
}

export default function AdminWebsite() {
  const [form, setForm] = useState(defaultForm());
  const [openSection, setOpenSection] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [subdomain, setSubdomain] = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    api.get('/api/admin/website')
      .then(res => {
        const data = res.data;
        setForm(f => ({
          ...defaultForm(), ...data,
          gallery_images: Array.isArray(data.gallery_images) ? data.gallery_images : [],
          testimonials: Array.isArray(data.testimonials) ? data.testimonials : [],
          working_hours: data.working_hours && typeof data.working_hours === 'object' ? data.working_hours : defaultHours(),
        }));
        setSubdomain(data.subdomain || '');
      })
      .catch(() => showToast('Failed to load settings', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const setHour = (day, field, val) => setForm(f => ({
    ...f, working_hours: { ...f.working_hours, [day]: { ...f.working_hours[day], [field]: val } },
  }));

  const addGallery = () => form.gallery_images.length < 10 && setForm(f => ({ ...f, gallery_images: [...f.gallery_images, ''] }));
  const setGallery = (i, v) => setForm(f => ({ ...f, gallery_images: f.gallery_images.map((x, j) => j === i ? v : x) }));
  const removeGallery = (i) => setForm(f => ({ ...f, gallery_images: f.gallery_images.filter((_, j) => j !== i) }));

  const addTestimonial = () => form.testimonials.length < 10 && setForm(f => ({
    ...f, testimonials: [...f.testimonials, { name:'', text:'', rating: 5, cefr:'B1' }],
  }));
  const setTest = (i, key, val) => setForm(f => ({
    ...f, testimonials: f.testimonials.map((t, j) => j === i ? { ...t, [key]: val } : t),
  }));
  const removeTest = (i) => setForm(f => ({ ...f, testimonials: f.testimonials.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/website', form);
      showToast('Changes saved successfully!', 'success');
    } catch {
      showToast('Failed to save. Please try again.', 'error');
    } finally { setSaving(false); }
  };

  const toggle = (i) => setOpenSection(openSection === i ? -1 : i);

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height: 300 }}>
      <div style={{ color: C.grey }}>Loading website settings…</div>
    </div>
  );

  const social = [
    { key:'instagram_url', label:'Instagram', icon:'📸', ph:'https://instagram.com/yourschool' },
    { key:'telegram_url', label:'Telegram', icon:'💬', ph:'https://t.me/yourschool' },
    { key:'facebook_url', label:'Facebook', icon:'👥', ph:'https://facebook.com/yourschool' },
    { key:'youtube_url', label:'YouTube', icon:'📺', ph:'https://youtube.com/@yourschool' },
    { key:'tiktok_url', label:'TikTok', icon:'🎵', ph:'https://tiktok.com/@yourschool' },
  ];

  return (
    <div style={{ fontFamily:'Inter, system-ui, sans-serif', background:'#f4f6f8', minHeight:'100vh' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position:'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.type === 'success' ? C.success : C.error,
          color:'#fff', padding:'12px 20px', borderRadius: 8,
          fontSize: 14, fontWeight: 500, boxShadow:'0 4px 12px rgba(0,0,0,0.15)',
        }}>{toast.msg}</div>
      )}

      {/* Header */}
      <div style={{ background: C.navy, padding:'16px 32px', display:'flex', alignItems:'center', gap: 12 }}>
        <span style={{ color:'#fff', fontSize: 18, fontWeight: 700 }}>Website Editor</span>
        <span style={{ color: C.teal, fontSize: 12, background:'rgba(0,180,216,0.15)', padding:'2px 8px', borderRadius: 4 }}>
          /admin/website
        </span>
      </div>

      <div style={{ display:'flex', gap: 0, maxWidth: 1400, margin:'0 auto' }}>
        {/* Left Panel — 60% */}
        <div style={{ flex:'0 0 60%', padding: 24, overflowY:'auto' }}>

          {/* 1. Basic Information */}
          <Section title="1. Basic Information" open={openSection === 0} onToggle={() => toggle(0)}>
            <Field label="School Tagline" hint={undefined}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>Tagline</span>
                <CharCounter value={form.tagline} max={150} />
              </div>
              <input style={inp()} maxLength={150} value={form.tagline} onChange={e => set('tagline', e.target.value)} placeholder="e.g. Speak English with confidence" />
            </Field>
            <Field label="School Description">
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>Description</span>
                <CharCounter value={form.description} max={500} />
              </div>
              <textarea style={inp({ height: 90, resize:'vertical' })} maxLength={500} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description of your school…" />
            </Field>
            <div style={{ display:'flex', gap: 12 }}>
              <Field label="Founded Year">
                <input type="number" style={inp({ width: 140 })} min={1900} max={2030} value={form.founded_year} onChange={e => set('founded_year', e.target.value)} />
              </Field>
            </div>
            <Field label="Hero Image URL">
              <div style={{ display:'flex', gap: 8 }}>
                <input style={inp()} value={form.hero_image_url} onChange={e => set('hero_image_url', e.target.value)} placeholder="https://example.com/hero.jpg" />
                {form.hero_image_url && <a href={form.hero_image_url} target="_blank" rel="noreferrer" style={{ color: C.teal, fontSize: 13, whiteSpace:'nowrap', alignSelf:'center' }}>Preview ↗</a>}
              </div>
            </Field>
          </Section>

          {/* 2. Address & Location */}
          <Section title="2. Address & Location" open={openSection === 1} onToggle={() => toggle(1)}>
            <Field label="Address Line 1"><input style={inp()} value={form.address_line1} onChange={e => set('address_line1', e.target.value)} placeholder="Street address" /></Field>
            <Field label="Address Line 2"><input style={inp()} value={form.address_line2} onChange={e => set('address_line2', e.target.value)} placeholder="Floor, suite, building (optional)" /></Field>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
              <Field label="City"><input style={inp()} value={form.city} onChange={e => set('city', e.target.value)} placeholder="Tashkent" /></Field>
              <Field label="Region"><input style={inp()} value={form.region} onChange={e => set('region', e.target.value)} placeholder="Tashkent Region" /></Field>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
              <Field label="Phone"><input style={inp()} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+998 71 123 4567" /></Field>
              <Field label="WhatsApp" hint="Include country code, e.g. +998901234567">
                <input style={inp()} value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="+998901234567" />
              </Field>
            </div>
            <Field label="Public Email"><input style={inp()} type="email" value={form.public_email} onChange={e => set('public_email', e.target.value)} placeholder="info@yourschool.com" /></Field>
            <Field label="Google Maps Embed URL" hint="Paste the embed src URL from Google Maps → Share → Embed a map">
              <textarea style={inp({ height: 60, resize:'vertical', fontSize: 11 })} value={form.google_maps_embed} onChange={e => set('google_maps_embed', e.target.value)} placeholder="https://www.google.com/maps/embed?pb=..." />
            </Field>
          </Section>

          {/* 3. Working Hours */}
          <Section title="3. Working Hours" open={openSection === 2} onToggle={() => toggle(2)}>
            <div style={{ display:'grid', gridTemplateColumns:"110px 80px 1fr 1fr", overflowX:"auto", marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.grey }}>Day</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.grey }}>Open</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.grey }}>Opens at</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.grey }}>Closes at</span>
            </div>
            {DAYS.map(d => {
              const h = form.working_hours[d] || { open: false, from:'09:00', to:'18:00' };
              return (
                <div key={d} style={{ display:'grid', gridTemplateColumns:"110px 80px 1fr 1fr", overflowX:"auto", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: C.navy }}>{DAY_LABELS[d]}</span>
                  <input type="checkbox" checked={h.open} onChange={e => setHour(d, 'open', e.target.checked)} style={{ width: 18, height: 18 }} />
                  <input type="time" style={inp({ opacity: h.open ? 1 : 0.4 })} disabled={!h.open} value={h.from} onChange={e => setHour(d, 'from', e.target.value)} />
                  <input type="time" style={inp({ opacity: h.open ? 1 : 0.4 })} disabled={!h.open} value={h.to} onChange={e => setHour(d, 'to', e.target.value)} />
                </div>
              );
            })}
          </Section>

          {/* 4. Social Media */}
          <Section title="4. Social Media" open={openSection === 3} onToggle={() => toggle(3)}>
            {social.map(s => (
              <Field key={s.key} label={`${s.icon} ${s.label}`}>
                <input style={inp()} value={form[s.key]} onChange={e => set(s.key, e.target.value)} placeholder={s.ph} />
              </Field>
            ))}
          </Section>

          {/* 5. Photo Gallery */}
          <Section title="5. Photo Gallery" open={openSection === 4} onToggle={() => toggle(4)}>
            <p style={{ fontSize: 12, color: C.grey, margin:'0 0 12px' }}>Photos shown on your public website. Use direct image URLs. Up to 10 photos.</p>
            {form.gallery_images.map((url, i) => (
              <div key={i} style={{ display:'flex', gap: 8, alignItems:'center', marginBottom: 10 }}>
                <img src={url || ''} alt="" onError={e => { e.target.src = 'https://placehold.co/60x60?text=?'; }}
                  style={{ width: 52, height: 52, objectFit:'cover', borderRadius: 6, border:`1px solid ${C.border}`, flexShrink: 0 }} />
                <input style={inp({ flex: 1 })} value={url} onChange={e => setGallery(i, e.target.value)} placeholder="https://example.com/photo.jpg" />
                <button onClick={() => removeGallery(i)} style={{ background: C.error, color:'#fff', border:'none', borderRadius: 6, padding:'6px 10px', cursor:'pointer', fontSize: 13 }}>✕</button>
              </div>
            ))}
            {form.gallery_images.length < 10 && (
              <button onClick={addGallery} style={{ background: C.teal, color:'#fff', border:'none', borderRadius: 6, padding:'8px 16px', cursor:'pointer', fontSize: 13, fontWeight: 600 }}>+ Add Photo</button>
            )}
          </Section>

          {/* 6. Student Testimonials */}
          <Section title="6. Student Testimonials" open={openSection === 5} onToggle={() => toggle(5)}>
            {form.testimonials.map((t, i) => (
              <div key={i} style={{ border:`1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 12, background:'#fafafa' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: C.navy }}>Testimonial #{i + 1}</span>
                  <button onClick={() => removeTest(i)} style={{ background:'none', border:'none', color: C.error, cursor:'pointer', fontSize: 13 }}>Remove</button>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 100px', gap: 10, marginBottom: 8 }}>
                  <Field label="Student Name"><input style={inp()} value={t.name} onChange={e => setTest(i, 'name', e.target.value)} placeholder="e.g. Aziz K." /></Field>
                  <Field label="CEFR Level">
                    <select style={inp()} value={t.cefr} onChange={e => setTest(i, 'cefr', e.target.value)}>
                      {['A1','A2','B1','B2','C1','C2'].map(l => <option key={l}>{l}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Testimonial Text">
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom: 2 }}>
                    <span style={{ fontSize: 12, color: C.navy }}>Text</span>
                    <CharCounter value={t.text} max={200} />
                  </div>
                  <textarea style={inp({ height: 70, resize:'vertical' })} maxLength={200} value={t.text} onChange={e => setTest(i, 'text', e.target.value)} placeholder="What did the student say about your school?" />
                </Field>
                <Field label="Rating"><StarRating value={t.rating} onChange={v => setTest(i, 'rating', v)} /></Field>
              </div>
            ))}
            {form.testimonials.length < 10 && (
              <button onClick={addTestimonial} style={{ background: C.teal, color:'#fff', border:'none', borderRadius: 6, padding:'8px 16px', cursor:'pointer', fontSize: 13, fontWeight: 600 }}>+ Add Testimonial</button>
            )}
          </Section>

          {/* 7. SEO Settings */}
          <Section title="7. SEO Settings" open={openSection === 6} onToggle={() => toggle(6)}>
            <p style={{ fontSize: 12, color: C.grey, margin:'0 0 12px' }}>These appear in Google search results.</p>
            <Field label="Meta Title">
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>Meta Title</span>
                <CharCounter value={form.meta_title} max={60} />
              </div>
              <input style={inp()} maxLength={60} value={form.meta_title} onChange={e => set('meta_title', e.target.value)} placeholder="Your School — English Language Centre" />
            </Field>
            <Field label="Meta Description">
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>Meta Description</span>
                <CharCounter value={form.meta_description} max={160} />
              </div>
              <textarea style={inp({ height: 70, resize:'vertical' })} maxLength={160} value={form.meta_description} onChange={e => set('meta_description', e.target.value)} placeholder="Learn English from A1 to C2. Expert tutors, AI practice, IELTS prep." />
            </Field>
            <Field label="Meta Keywords" hint="Comma-separated, e.g. english school, ielts, uzbekistan">
              <input style={inp()} value={form.meta_keywords} onChange={e => set('meta_keywords', e.target.value)} placeholder="english school, ielts prep, tashkent" />
            </Field>
          </Section>

          {/* 8. Website Settings */}
          <Section title="8. Website Settings" open={openSection === 7} onToggle={() => toggle(7)}>
            <div style={{ display:'flex', flexDirection:'column', gap: 16 }}>
              <Toggle checked={form.website_enabled} onChange={v => set('website_enabled', v)} label="Enable public website" />
              <Toggle checked={form.show_prices_public} onChange={v => set('show_prices_public', v)} label="Show prices publicly" />
              <Toggle checked={form.show_tutors_public} onChange={v => set('show_tutors_public', v)} label="Show tutor profiles publicly" />
              <Toggle checked={form.website_currency_converter !== false} onChange={v => set('website_currency_converter', v)} label="Show currency converter on website" />
              <Toggle checked={form.registration_open} onChange={v => set('registration_open', v)} label="Registration open (students can enroll)" />
              {!form.registration_open && (
                <Field label="Closed Registration Message" hint="Shown to students when registration is closed">
                  <input style={inp()} value={form.registration_message} onChange={e => set('registration_message', e.target.value)} placeholder="We are currently not accepting new students. Check back soon!" />
                </Field>
              )}
            </div>
          </Section>

          {/* Save Button */}
          <button onClick={save} disabled={saving} style={{
            width:'100%', padding:'14px', background: saving ? C.grey : C.teal,
            color:'#fff', border:'none', borderRadius: 8, fontSize: 15, fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer', marginTop: 8, letterSpacing: 0.3,
          }}>
            {saving ? 'Saving…' : 'Save All Changes'}
          </button>
        </div>

        {/* Right Panel — 40% */}
        <div style={{ flex:'0 0 40%', padding: 24 }}>
          <LivePreview form={form} subdomain={subdomain} />
        </div>
      </div>
    </div>
  );
}
